import React from 'react';
import { Container, Typography, Paper, Box, Grid, Button, Card, CardContent, CircularProgress, Divider } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';           // Döküman ikonu
import CloudUploadIcon from '@mui/icons-material/CloudUpload';        // Yükleme ikonu
import FolderSharedIcon from '@mui/icons-material/FolderShared';       // Paylaşım/Personel Dosyası ikonu
import PolicyIcon from '@mui/icons-material/Policy';               // Politika ikonu
import { useTranslation } from 'react-i18next';

const DocumentManagementPage: React.FC = () => {
  const { t } = useTranslation(['humanResources', 'common2']);
  const hr = (key: string, defaultValue: string) => t(key, { ns: 'humanResources', defaultValue });

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      position: 'relative',
      overflowX: 'hidden'
    }}>
      <Box 
        sx={{ 
          height: 'calc(100vh - 80px)', 
          overflowY: 'auto', 
          overflowX: 'hidden',
          width: '100%',
          paddingRight: '10px',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            marginLeft: '10px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
            '&:hover': {
              background: '#555'
            }
          }
        }}
      >
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box sx={{ pb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <ArticleIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                {hr('pages.document.title', 'Document Management System')}
              </Typography>
            </Box>
            
            <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 4 }}>
              {hr('pages.document.subtitle', 'This page is under construction. The document management module will be available soon.')}
            </Typography>

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {hr('pages.shared.developmentProcess', 'Development Process')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ my: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{hr('pages.shared.analysis', 'Analysis')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <CircularProgress variant="determinate" value={80} color="success" />
                          <Typography variant="body2" sx={{ ml: 2 }}>{hr('pages.shared.inProgress', 'In Progress')}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{hr('pages.shared.design', 'Design')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <CircularProgress variant="determinate" value={20} color="primary" />
                          <Typography variant="body2" sx={{ ml: 2 }}>{hr('pages.shared.initialStage', 'Initial Stage')}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{hr('pages.shared.development', 'Development')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <CircularProgress variant="determinate" value={0} color="warning" />
                          <Typography variant="body2" sx={{ ml: 2 }}>{hr('pages.shared.notStarted', 'Not Started')}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {hr('pages.shared.plannedFeatures', 'Module Features (Planned)')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <CloudUploadIcon sx={{ fontSize: 40, mb: 1, color: 'secondary.main' }} />
                      <Typography variant="h6" gutterBottom>
                        {hr('pages.document.features.storageTitle', 'Centralized Document Storage')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {hr('pages.document.features.storageDesc', 'Secure storage and management of all HR documents (contracts, policies, forms).')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <FolderSharedIcon sx={{ fontSize: 40, mb: 1, color: 'success.main' }} />
                      <Typography variant="h6" gutterBottom>
                        {hr('pages.document.features.personnelFilesTitle', 'Personnel File Management')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {hr('pages.document.features.personnelFilesDesc', 'Create digital personnel files for each employee, upload required documents, and manage access.')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <PolicyIcon sx={{ fontSize: 40, mb: 1, color: 'info.main' }} />
                      <Typography variant="h6" gutterBottom>
                        {hr('pages.document.features.policyTitle', 'Policy and Regulation Publishing')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {hr('pages.document.features.policyDesc', 'Easily publish company policies and updates and notify employees.')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mb: 4, bgcolor: 'rgba(0, 150, 136, 0.04)' }}> 
              <Typography variant="h6" gutterBottom>
                {hr('pages.document.whyTitle', 'Why a Document Management System?')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="body1" paragraph>
                {hr('pages.document.whyIntro', 'Document management in HR processes is vital for maintaining order and meeting legal requirements. With this system:')}
              </Typography>
              
              <Box component="ul" sx={{ pl: 2 }}>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.document.benefits.paperless', 'Paper waste is reduced, and the need for physical archiving is eliminated.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.document.benefits.access', 'Fast and easy access to documents is provided.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.document.benefits.security', 'Document security and access control are improved.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.document.benefits.compliance', 'Legal compliance and audit processes become easier.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.document.benefits.versionControl', 'Version control ensures the use of up-to-date documents.')}</Typography>
                </Box>
              </Box>
              
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                {hr('pages.document.conclusion', 'Our document management module increases operational efficiency by enabling your HR department to manage all documents centrally, securely, and accessibly.')}
              </Typography>
            </Paper>

            <Box sx={{ textAlign: 'center', mt: 4, pb: 4 }}>
              <Button variant="contained" color="primary" disabled>
                {hr('pages.shared.moduleAvailableWhenReady', 'The Module Will Be Available When Ready')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default DocumentManagementPage; 